import React, { useState } from 'react';
import {
  Button, Card, Col, Dropdown, Empty, Modal, Row, Select, Space, Table, Tabs, Typography,
} from 'antd';
import {
  CalendarOutlined, CarOutlined, CopyOutlined, DownOutlined, EditOutlined, FileSearchOutlined,
  FlagOutlined, HomeOutlined, PlusOutlined, SwapOutlined, UserOutlined,
} from '@ant-design/icons';
import SoftTag, { OfferTypeTag } from '../components/SoftTag';
import InfoPill from '../components/InfoPill';
import EntityCommentsCard from '../components/EntityCommentsCard';
import InquiryItemModal from '../modals/InquiryItemModal';
import InquiryHeaderEditModal from '../modals/InquiryHeaderEditModal';
import {
  INQUIRY_STATUSES, PRIORITY_TONES, getInquiryStatusTone, getQuoteStatusTone, getOfferStatusTone,
} from '../constants/statuses';
import { buildInquiryItemColumns } from '../utils/inquiryItemColumns';
import { isReadyForOffer } from '../utils/quoteHelpers';
import { formatDate, formatDateTime } from '../utils/formatters';
import { brandSemantic } from '../config/brand';
import { useI18n } from '../i18n';

const { Option } = Select;
const { confirm } = Modal;
const { Title } = Typography;

// ── Inquiry status selector styled like existing StatusSelect ────────────────
function InquiryStatusSelect({ value, onChange }) {
  const { t } = useI18n();
  const tone = getInquiryStatusTone(value);
  return (
    <Select
      value={value}
      onChange={onChange}
      size="small"
      variant="borderless"
      className="status-tag-select"
      style={{
        width: Math.min(Math.max((value || '').length * 7 + 34, 130), 280),
        '--status-color': tone.color,
        '--status-bg': tone.background,
        '--status-border': tone.border,
      }}
      dropdownMatchSelectWidth={false}
    >
      {INQUIRY_STATUSES.map((s) => {
        const st = getInquiryStatusTone(s);
        return (
          <Option key={s} value={s}>
            <span style={{ color: st.color, fontWeight: 500 }}>{t(s)}</span>
          </Option>
        );
      })}
    </Select>
  );
}

// ── Main InquiryDetailPage ───────────────────────────────────────────────────
export default function InquiryDetailPage({
  inquiry,
  leads = [],
  categories = [],
  users = [],
  comments = [],
  existingQuote = null,
  relatedQuotes = [],
  offers = [],
  suppliers = [],
  onBack,
  onUpdateInquiry,
  onCreateQuote,
  onOpenQuote,
  onOpenOffer,
  openLeadProfile,
  onAddComment,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onOpenCategoryDetail,
  onCreateQuoteForItem,
  onCreateOfferFromQuotes,
  onCopyInquiry,
  t: tProp,
}) {
  const { t: tHook } = useI18n();
  const t = tProp || tHook;
  const [selectedQuoteIds, setSelectedQuoteIds] = useState([]);
  const [quoteTypeFilter, setQuoteTypeFilter] = useState('all');
  const [selectedInquiryItemIds, setSelectedInquiryItemIds] = useState([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [headerEditOpen, setHeaderEditOpen] = useState(false);

  if (!inquiry) return null;

  const lead = leads.find((l) => l.id === inquiry.clientId) || null;
  const priorityTone = PRIORITY_TONES[inquiry.priority] || PRIORITY_TONES.Medium;
  const items = inquiry.items || [];
  const selectedQuotesForOffer = relatedQuotes.filter((quote) => selectedQuoteIds.includes(quote.id));
  const canBulkCreateOffer = selectedQuotesForOffer.length > 0
    && selectedQuotesForOffer.every((quote) => isReadyForOffer(quote, suppliers) && !quote.convertedOfferId);
  const deliveryAddress = inquiry.deliveryAddress || lead?.address || '—';

  const quoteRows = relatedQuotes.map((quote) => ({
    id: quote.id,
    recordId: quote.id,
    recordType: 'Internal Quote',
    client: quote.client,
    categoryName: quote.categoryName || quote.productCategory,
    productName: quote.productName,
    date: quote.createdAt || quote.date,
    status: quote.status,
    statusTone: getQuoteStatusTone(quote.status),
    isQuoteRow: true,
    isConverted: Boolean(quote.convertedOfferId),
    onOpen: () => onOpenQuote?.(quote),
  }));

  // An Offer can be built from several Quotes (one per product line), but it
  // is still a single document — group its source Quotes so it appears here
  // exactly once, with their products combined, instead of once per Quote.
  const quotesByOfferId = new Map();
  relatedQuotes.forEach((quote) => {
    if (!quote.convertedOfferId) return;
    const group = quotesByOfferId.get(quote.convertedOfferId) || [];
    group.push(quote);
    quotesByOfferId.set(quote.convertedOfferId, group);
  });
  const offerRows = Array.from(quotesByOfferId.entries()).flatMap(([offerId, sourceQuotes]) => {
    const offer = offers.find((item) => item.id === offerId);
    if (!offer) return [];
    const categoryNames = Array.from(new Set(sourceQuotes.map((quote) => quote.categoryName || quote.productCategory).filter(Boolean)));
    const productNames = Array.from(new Set(sourceQuotes.map((quote) => quote.productName).filter(Boolean)));
    return [{
      id: offer.id,
      recordId: offer.id,
      recordType: 'Sales Quote',
      client: offer.client,
      categoryName: categoryNames.join(', '),
      productName: productNames.join(', '),
      date: offer.date,
      status: offer.status,
      statusTone: getOfferStatusTone(offer.status),
      isQuoteRow: false,
      onOpen: () => onOpenOffer?.(offer),
    }];
  });
  const quoteAndOfferRecords = [...quoteRows, ...offerRows];
  const filteredQuoteAndOfferRecords = quoteTypeFilter === 'all'
    ? quoteAndOfferRecords
    : quoteAndOfferRecords.filter((record) => record.recordType === quoteTypeFilter);

  const quoteAndOfferColumns = [
    {
      title: t('ID'),
      dataIndex: 'recordId',
      key: 'recordId',
      render: (recordId, record) => (
        <Button type="link" onClick={record.onOpen} style={{ padding: 0, fontWeight: 600 }}>
          {recordId}
        </Button>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'recordType',
      key: 'recordType',
      width: 140,
      render: (value) => (
        <SoftTag
          tone={value === 'Internal Quote'
            ? { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder }
            : { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder }}
        >
          {t(value)}
        </SoftTag>
      ),
    },
    { title: t('Client Name'), dataIndex: 'client', key: 'client' },
    { title: t('Category'), dataIndex: 'categoryName', key: 'categoryName' },
    { title: t('Product'), dataIndex: 'productName', key: 'productName' },
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (value) => (value ? formatDate(String(value).slice(0, 10)) : '—'),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (value, record) => <SoftTag tone={record.statusTone}>{t(value)}</SoftTag>,
    },
  ];

  const itemColumns = buildInquiryItemColumns({
    t,
    onCategoryClick: (record) => onOpenCategoryDetail?.(record),
    showSourcingStatus: true,
    suppliers,
    onEdit: (record) => { setEditingItem(record); setItemModalOpen(true); },
    onDelete: (record) => {
      confirm({
        title: t('Delete this item?'),
        content: t('Are you sure you want to delete this entry?'),
        okText: t('Delete'),
        cancelText: t('Cancel'),
        onOk: () => onDeleteItem?.(record.id),
      });
    },
  });

  const tabItems = [
    {
      key: 'products',
      label: t('Products'),
      children: (
        <Card
          extra={(
            <Space>
              <Button icon={<PlusOutlined />} onClick={() => { setEditingItem(null); setItemModalOpen(true); }}>
                {t('Add Product')}
              </Button>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                disabled={selectedInquiryItemIds.length === 0 || selectedInquiryItemIds.some((itemId) => {
                  const item = items.find((i) => i.id === itemId);
                  return item?.wizardResult == null
                    || item?.sourcingStatus === 'Quote Created'
                    || relatedQuotes.some((quote) => quote.inquiryItemId === itemId && !quote.archived);
                })}
                onClick={() => {
                  onCreateQuoteForItem?.(inquiry.id, selectedInquiryItemIds);
                  setSelectedInquiryItemIds([]);
                }}
              >
                {t('Create Internal Quote')}{selectedInquiryItemIds.length > 0 ? ` (${selectedInquiryItemIds.length})` : ''}
              </Button>
            </Space>
          )}
          style={{ borderRadius: 16 }}
        >
          {items.length === 0 ? (
            <Empty description={t('No products added yet')}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingItem(null); setItemModalOpen(true); }}>
                {t('Add Product')}
              </Button>
            </Empty>
          ) : (
            <Table
              rowKey="id"
              dataSource={items}
              columns={itemColumns}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys: selectedInquiryItemIds,
                onChange: setSelectedInquiryItemIds,
                getCheckboxProps: (record) => ({
                  disabled: record.wizardResult == null
                    || record.sourcingStatus === 'Quote Created'
                    || relatedQuotes.some((quote) => quote.inquiryItemId === record.id && !quote.archived),
                }),
              }}
              pagination={false}
              size="middle"
              scroll={{ x: 'max-content' }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'quotes',
      label: t('Quotes'),
      children: (
        <Card
          extra={relatedQuotes.length > 0 && (
            <Space>
              <Select value={quoteTypeFilter} onChange={setQuoteTypeFilter} style={{ width: 170 }}>
                <Option value="all">{t('All Types')}</Option>
                <Option value="Internal Quote">{t('Internal Quote')}</Option>
                <Option value="Sales Quote">{t('Sales Quote')}</Option>
              </Select>
              <Button
                type="primary"
                icon={<SwapOutlined />}
                disabled={!canBulkCreateOffer}
                onClick={() => {
                  onCreateOfferFromQuotes?.(selectedQuoteIds);
                  setSelectedQuoteIds([]);
                }}
              >
                {t('Create Sales Quote')}{selectedQuoteIds.length > 0 ? ` (${selectedQuoteIds.length})` : ''}
              </Button>
            </Space>
          )}
          style={{ borderRadius: 16 }}
        >
          {relatedQuotes.length === 0 ? (
            <Empty description={t('No quotes yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              rowKey="id"
              dataSource={filteredQuoteAndOfferRecords}
              rowSelection={{
                selectedRowKeys: selectedQuoteIds,
                onChange: setSelectedQuoteIds,
                getCheckboxProps: (record) => ({ disabled: !record.isQuoteRow || record.isConverted }),
              }}
              columns={quoteAndOfferColumns}
              pagination={false}
              size="middle"
              scroll={{ x: 'max-content' }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'notes',
      label: t('Notes'),
      children: (
        <EntityCommentsCard
          entityId={inquiry.id}
          entityName={inquiry.id}
          getLatestComment={(entityId) => {
            const entityComments = (comments || []).filter((c) => c.entityId === entityId);
            return entityComments.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0] || null;
          }}
          formatDateTime={formatDateTime}
          onOpenComments={(entityId, entityName) => onAddComment?.({ entityId, entityName })}
        />
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button icon={<HomeOutlined />} onClick={onBack}>
          {t('Back to List')}
        </Button>
        <Space>
          <Button icon={<EditOutlined />} onClick={() => setHeaderEditOpen(true)}>
            {t('Edit')}
          </Button>
          <Dropdown
            menu={{
              items: [
                { key: 'all', label: t('Copy all') },
                { key: 'products', label: t('Copy only Products') },
                { key: 'productsAndQuotes', label: t('Copy Products and Internal Quotes') },
              ],
              onClick: ({ key }) => onCopyInquiry?.(inquiry.id, key),
            }}
          >
            <Button icon={<CopyOutlined />}>
              {t('Copy')} <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
          {items.length === 0 && !existingQuote && inquiry.status === 'Sent to Procurement' && (
            <Button
              type="primary"
              icon={<FileSearchOutlined />}
              onClick={() => onCreateQuote?.(inquiry)}
            >
              {t('Create Quote')}
            </Button>
          )}
        </Space>
      </div>

      {/* compact header card */}
      <Card style={{ borderRadius: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <Title level={3} style={{ margin: 0 }}>{inquiry.id}</Title>
          <Space size={8}>
            <OfferTypeTag type="Inquiry" />
            <InquiryStatusSelect
              value={inquiry.status}
              onChange={(val) => onUpdateInquiry?.(inquiry.id, { status: val })}
            />
          </Space>
        </div>

        <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
          <Col xs={24} sm={12} md={12} lg={8}>
            <InfoPill icon={<UserOutlined />} label={t('Client Name')} value={inquiry.client} />
          </Col>
          <Col xs={24} sm={12} md={12} lg={8}>
            <InfoPill icon={<EditOutlined />} label={t('Created By')} value={inquiry.createdBy || '—'} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={8}>
            <InfoPill
              icon={<FlagOutlined />}
              label={t('Priority')}
              value={<SoftTag tone={priorityTone} style={{ fontSize: 12, padding: '1px 8px' }}>{t(inquiry.priority)}</SoftTag>}
            />
          </Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} sm={24} md={12} lg={8}>
            <InfoPill icon={<HomeOutlined />} label={t('Delivery Address')} value={deliveryAddress} />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <InfoPill
              icon={<CalendarOutlined />}
              label={t('Deadline')}
              value={inquiry.neededBefore ? formatDate(inquiry.neededBefore) : '—'}
              accent={brandSemantic.error}
            />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <InfoPill
              icon={<CarOutlined />}
              label={t('Requested Delivery Date')}
              value={inquiry.requestedDeliveryDate ? formatDate(inquiry.requestedDeliveryDate) : '—'}
            />
          </Col>
        </Row>
      </Card>

      <Tabs items={tabItems} />

      {/* add / edit inquiry item modal */}
      <InquiryItemModal
        open={itemModalOpen}
        onCancel={() => { setItemModalOpen(false); setEditingItem(null); }}
        onSave={(itemData) => {
          if (editingItem) onUpdateItem?.(editingItem.id, itemData);
          else onAddItem?.(itemData);
          setItemModalOpen(false);
          setEditingItem(null);
        }}
        item={editingItem}
        categories={categories}
      />

      {/* edit inquiry header modal */}
      <InquiryHeaderEditModal
        open={headerEditOpen}
        inquiry={inquiry}
        leads={leads}
        users={users}
        onCancel={() => setHeaderEditOpen(false)}
        onSave={(patch) => {
          onUpdateInquiry?.(inquiry.id, patch);
          setHeaderEditOpen(false);
        }}
      />
    </div>
  );
}
